/**
 * <biosphere-count-up> — runs a figure up to its own value the first time it comes
 * into view.
 *
 * It owns its motion in JS, against the rule in assets/CLAUDE.md that motion is
 * CSS-owned, because no CSS animates text content while leaving the real number in the
 * document: drawing the digits in a pseudo-element takes the figure out of the DOM,
 * and a scroll-driven timeline would run the count backwards on the way up the page.
 * The rule's intent is kept — reduced motion is checked here, and checked first.
 *
 * Liquid has already written the final value, and every guard failure leaves it
 * exactly where it is: nothing here can make the figure wrong, only late.
 *
 * Once per element — the observer disconnects on first intersection.
 */
class BiosphereCountUp extends HTMLElement {
  #observer;

  connectedCallback() {
    const target = Number(this.dataset.target);
    const duration = Number(this.dataset.duration);

    // Anything unreadable, and the server-rendered figure simply stands.
    if (!Number.isFinite(target) || !(duration > 0)) return;
    if (!("IntersectionObserver" in window)) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const decimals = Number(this.dataset.decimals) || 0;

    // Grouped the way the storefront's language groups numbers, not the
    // way this file would guess: 1,200 and 1.200 are the same figure.
    const format = new Intl.NumberFormat(document.documentElement.lang || [], {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    // Reset before the first frame, not on the first frame, so the figure
    // never shows its final value and then jumps back to zero.
    this.textContent = format.format(0);

    this.#observer = new IntersectionObserver(
      ([entry], observer) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        this.#count(target, duration, format);
      },
      // Half of it showing, so the count starts when the figure is
      // genuinely being looked at rather than clipped by the fold.
      { threshold: 0.5 },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /** Eased out, so the figure decelerates onto its value rather than stopping dead. */
  #count(target, duration, format) {
    let start = null;

    const step = (now) => {
      start ??= now;

      const elapsed = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - elapsed, 3);

      // The last frame writes the target itself: easing arrives
      // asymptotically, and a dose that settles on 399 is a wrong dose.
      this.textContent = format.format(elapsed === 1 ? target : target * eased);

      if (elapsed < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }
}

if (!customElements.get("biosphere-count-up")) {
  customElements.define("biosphere-count-up", BiosphereCountUp);
}
